import { Combobox } from '@reach/combobox';
import '@reach/combobox/styles.css';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'urql';
import useDebounce from 'app/hooks/useDebounce';
import { useLocalStorage } from 'app/hooks/useLocalStorage';
import { createTrackingOnItemImpression, trackCustomEvent } from '../../../utils/statistics/clientStatistics';
import { listSearchEvent, searchQueryEvent, searchShowAll } from '../../../utils/statistics/customEvents';
import SEARCH_QUERY from './AutoComplete.graphql';
import { Form, SearchInput, SearchLabel, SubmitButton, SearchIcon } from './AutoCompleteResult.styles';
import PopOver from './PopOver';
import { getContentId, getId, getUrl, sortSearchResult, value } from './helpers';
import { generateListTracking } from '../../../utils/contentoUtils';

const INPUT_SEARCH_PLACEHOLDER_TEXT = 'Sök på SVT Play';
const INPUT_SEARCH_PLACEHOLDER_TEXT_FOCUSED = 'Sök på titlar, kategorier, medverkande';

function usePrevious(previousValue) {
    const ref = useRef();
    useEffect(() => {
        if (previousValue) {
            ref.current = previousValue;
        }
    });

    return ref.current;
}

const AutoCompleteSearch = ({ activeMobileSearch, updateActiveQuery, contentoAbTestVariants }) => {
    const router = useRouter();
    const [term, setTerm] = useState('');
    const debouncedTerm = useDebounce(term, 300);
    const [hasFocus, setHasFocus] = useState(false);
    const debouncedHasFocus = useDebounce(hasFocus, 100);
    const ref = useRef(null);

    // Note that changes to local storage from other places doesn't update the value of `searchHistory``
    // To force update, you have to reload the page.
    const [searchHistory, setSearchHistory] = useLocalStorage('searchHistory', []);
    const searchClickHistory = searchHistory.map((click) => ({ id: click.id }));

    useEffect(() => {
        if (activeMobileSearch) {
            ref?.current?.focus();
        }
    }, [activeMobileSearch, ref]);

    useEffect(() => {
        if (debouncedTerm) {
            trackCustomEvent(searchQueryEvent(debouncedTerm));
        }
    }, [debouncedTerm]);

    // Since we don't want to track unintended focus on the search input (i.e. the user is using tab to navigate)
    // we'll use a slightly longer timeout for this
    useEffect(() => {
        if (hasFocus) {
            const handler = setTimeout(() => {
                trackCustomEvent(searchQueryEvent());
            }, 500);

            return () => {
                clearTimeout(handler);
            };
        }

        return () => {};
    }, [hasFocus]);

    const [result] = useQuery({
        query: SEARCH_QUERY,
        variables: {
            querystring: term.slice(0, 100),
            searchClickHistory: searchClickHistory.slice(0, 20),
            abTestVariants: contentoAbTestVariants,
        },
        pause: !hasFocus,
    });

    const { data, fetching, error } = result;

    // We use previousdata to display the previous result while fetching
    // Otherwise the the search box would be blank between fetches
    const previousData = usePrevious(data);
    const previousResult =
        (fetching || error) && previousData?.searchPage?.flat ? previousData?.searchPage?.flat : undefined;
    const newResult = data?.searchPage?.flat;

    const searchResult = fetching || error ? sortSearchResult(previousResult) : sortSearchResult(newResult);

    const handleChange = (e) => {
        updateActiveQuery(e.target.value);
        setTerm(e.target.value);
    };

    const handleSubmit = (e) => {
        updateActiveQuery('');
        if (ref?.current) {
            ref.current.blur();
        }
        e.preventDefault();

        router.push({
            pathname: '/sok',
            query: { q: term },
        });
    };

    const onAllMatchesClick = () => {
        updateActiveQuery('');
        if (ref?.current) {
            ref.current.blur();
        }
        trackCustomEvent(searchShowAll(term));
    };

    const handleOnFocus = () => {
        updateActiveQuery('');
        updateActiveQuery(term);
        setHasFocus(true);
    };

    const handleOnBlur = () => {
        setHasFocus(false);
    };

    const listTracking = generateListTracking(searchResult);
    const onItemImpression = createTrackingOnItemImpression(listTracking, 1);

    const handleSelect = (searchVal) => {
        let listItemIndex = 1;
        let selectedItem;

        searchResult.hits.forEach((hit) => {
            if (value(hit) === searchVal) {
                selectedItem = {
                    listItemIndex,
                    contentId: getContentId(hit),
                    id: getId(hit),
                    url: getUrl(hit),
                };
            }

            listItemIndex++;
        });

        if (selectedItem) {
            const newValue = [
                { id: selectedItem.id, createdAt: Date.now() },
                ...searchHistory.filter((click) => click.id !== selectedItem.id).slice(0, 100),
            ];
            setSearchHistory(newValue);

            trackCustomEvent(listSearchEvent(listTracking, selectedItem, term));

            router.push(selectedItem.url);
            setTerm('');
        }
    };

    const hasResult = searchResult?.hits && searchResult.hits.length > 0;
    const displayPopOver = hasResult && debouncedHasFocus;

    return (
        <Form
            action="/sok"
            method="GET"
            onSubmit={handleSubmit}
            data-rt="autocomplete-search-form"
            autoComplete="off"
            className="lp_search"
        >
            <Combobox
                id="combobox"
                openOnFocus={searchClickHistory.length > 0}
                onSelect={(item) => {
                    handleSelect(item);
                }}
                aria-label="Sök. Sökförslag kommer att visas under sökfältet."
            >
                <SearchInput
                    onChange={(e) => {
                        handleChange(e);
                    }}
                    id="search"
                    onFocus={handleOnFocus}
                    onBlur={handleOnBlur}
                    placeholder={
                        hasFocus ? INPUT_SEARCH_PLACEHOLDER_TEXT_FOCUSED : INPUT_SEARCH_PLACEHOLDER_TEXT
                    }
                    selectOnClick
                    name="q"
                    value={term}
                    ref={ref}
                />
                <SubmitButton className="lp_button" type="submit" title="Sök på svtplay.se" aria-label="Sök">
                    <SearchIcon aria-hidden="true" />
                    <SearchLabel>Sök</SearchLabel>
                </SubmitButton>
                {displayPopOver && (
                    <PopOver
                        result={searchResult}
                        suggestionClick={handleSelect}
                        term={term}
                        onAllMatchesClick={onAllMatchesClick}
                        onItemImpression={onItemImpression}
                    />
                )}
            </Combobox>
        </Form>
    );
};

AutoCompleteSearch.propTypes = {
    updateActiveQuery: PropTypes.func.isRequired,
    contentoAbTestVariants: PropTypes.array,
};

export default AutoCompleteSearch;
